import {
  Component,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  MatError,
  MatFormField,
  MatHint,
  MatLabel,
  MatPrefix,
  MatSuffix,
} from '@angular/material/form-field';
import { getTargetScheduleInterval } from '../../../api/_lib/schedule';
import { ApplyPipe } from '../../apply.pipe';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  injectFormElements,
  retrieveFormElement,
} from '../../forms/forms.util';
import { ErrorChangesPipe } from '../../forms/error-changes.pipe';
import { MatInput } from '@angular/material/input';
import { Staff } from '../../../api/staff/staff.model';
import { differenceInYears } from 'date-fns';
import { ErrorStateMatcher } from '@angular/material/core';
import { ObservableErrorStateMatcher } from '../../forms/observable-error-state';
import {
  catchError,
  combineLatestWith,
  delay,
  filter,
  NEVER,
  of,
  shareReplay,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivityRole } from '../../../api/activity/lib/activity.interfaces';
import { Target } from '../../target.util';
import { WageSchemaError } from '../../../api/wage-schema/lib/wage-schema.util';
import { WageSchemaService } from '../../../api/wage-schema/wage-schema.service';
import { i18nICUAttribute } from '../../i18n/i18n-attributes';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { ContractType } from '../../../api/contract/lib/contract.interfaces';
import { MatIcon } from '@angular/material/icon';
import { NoopValueAccessorDirective } from '../../components/noop-value-accessor.directive';

@Component({
  selector: 'app-hourly-pay-form-field',
  standalone: true,
  imports: [
    MatFormField,
    ApplyPipe,
    ErrorChangesPipe,
    ReactiveFormsModule,
    MatInput,
    MatTooltip,
    MatHint,
    MatError,
    MatLabel,
    MatSlideToggle,
    MatSuffix,
    MatIcon,
    MatPrefix,
  ],
  hostDirectives: [NoopValueAccessorDirective],
  templateUrl: './hourly-pay-form-field.component.html',
  styleUrl: './hourly-pay-form-field.component.scss',
})
export class HourlyPayFormFieldComponent implements OnInit {
  private readonly wageSchemaService = inject(WageSchemaService);
  protected readonly getTargetScheduleInterval = getTargetScheduleInterval;
  hourlyPay = new FormControl(0);
  formElements = injectFormElements(FormControl);
  activeSchema = signal<string | null>(null);
  value = input<number>();
  staff = input.required<Staff | null | undefined>();
  role = input.required<ActivityRole | null | undefined>();
  target = input.required<Target | null | undefined>();
  auto = input<boolean>();
  onAutoChange = output<boolean>();
  private readonly wage$ = toObservable(this.staff).pipe(
    combineLatestWith(toObservable(this.role), toObservable(this.target)),
    switchMap(([staff, role, target]) =>
      !!(staff && role && target)
        ? this.wageSchemaService
            .getWage(staff!, role!, getTargetScheduleInterval(target!).start)
            .pipe(
              catchError((err) => this.syncHourlyPayError(err)),
              tap(() => this.syncHourlyPayError()),
            )
        : of(null),
    ),
    takeUntilDestroyed(),
    shareReplay(1),
  );
  protected readonly observableErrorState: ErrorStateMatcher =
    new ObservableErrorStateMatcher(this.wage$);
  protected readonly automaticWage = new FormControl(true);

  constructor() {
    this.automaticWage.valueChanges
      .pipe(takeUntilDestroyed(), withLatestFrom(this.wage$))
      .subscribe(([automatic, wage]) => {
        if (automatic) {
          this.updateWage(wage);
        }
        this.onAutoChange.emit(!!automatic);
      });
    // Set wage proposal when it comes in
    this.wage$
      .pipe(
        takeUntilDestroyed(),
        filter(() => this.hourlyPay.pristine),
      )
      .subscribe((wage) => {
        this.updateWage(wage);
      });
    // Update auto state when input changes
    effect(() => {
      const auto = this.auto();
      if (auto !== undefined && auto !== this.automaticWage.value) {
        this.onAutoChange.emit(auto);
        this.automaticWage.setValue(auto);
      }
    });
    // Update value when input changes
    effect(() => {
      const value = this.value();
      if (value !== undefined && !this.automaticWage.value) {
        this.hourlyPay.markAsDirty();
        this.hourlyPay.setValue(value);
      }
    });
  }

  ngOnInit() {
    // Get the bound formControl
    this.hourlyPay =
      retrieveFormElement<typeof this.hourlyPay>(
        this.formElements,
        FormControl,
      ) ?? this.hourlyPay;
  }

  private updateWage(wage: { wage: number; name: string } | null) {
    if (!wage) {
      this.hourlyPay.setValue(null);
      this.activeSchema.set(null);
    } else {
      this.hourlyPay.markAsPristine();
      this.hourlyPay.markAsUntouched();
      this.hourlyPay.setValue(wage.wage);
      this.activeSchema.set(wage.name);
    }
  }

  private syncHourlyPayError(err?: unknown) {
    if (err instanceof WageSchemaError) {
      this.hourlyPay.setErrors({
        ...this.hourlyPay.errors,
        wageSchema: err.message + ' ' + $localize`Wage calculation impossible.`,
      });
      this.hourlyPay.markAsDirty();
      this.hourlyPay.markAsTouched();
      return NEVER;
    } else {
      // Unknown error occurred, strip any possible wageSchema errors and rethrow.
      let errors = this.hourlyPay.errors;
      delete errors?.['wageSchema'];
      if (!Object.keys(errors ?? {}).length) errors = null;
      this.hourlyPay.setErrors(errors);
      // Throw the error in catchError mode
      if (err) throw err;
      // Return something from all code paths; use never to ignore return type
      return err as never;
    }
  }

  protected readonly getAge = (date: Date | undefined, refDate = new Date()) =>
    date ? differenceInYears(refDate, date) : '?';
  protected readonly i18nICUAttribute = i18nICUAttribute;
  protected readonly ContractType = ContractType;
  protected readonly console = console;
  protected readonly delay = delay;
}
